import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import {
  ArrowLeft,
  Trophy,
  Sparkles,
  Star,
  BookOpen,
  Award,
  Loader2,
} from "lucide-react";

export default function TeacherPerformance() {
  const { id } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get(`/teachers/${id}`), api.get(`/teachers/${id}/reviews`)])
      .then(([t, r]) => {
        if (cancelled) return;
        setTeacher(t.data);
        setReviews(r.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" style={{ background: "#FFF7ED" }}>
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#13A699" }} />
      </main>
    );
  }
  if (!teacher) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" style={{ background: "#FFF7ED" }} data-testid="teacher-not-found">
        <p style={{ color: "#13A699" }}>Mentor not found.</p>
      </main>
    );
  }

  const rating = teacher.rating_avg || 0;
  const ratingCount = teacher.rating_count || 0;
  const points = teacher.points || 0;
  const badges = teacher.badges || [];
  const mentorChampCount = badges.filter((b) => b === "mentor_champ").length;
  const eliteMentor = badges.includes("elite_mentor");
  const coursesCompleted = teacher.courses_completed || 0;
  const photoUrl = teacher.photo_path ? `${API}/files/${teacher.photo_path}` : null;

  // Rating distribution
  const dist = [5, 4, 3, 2, 1].map((s) => ({
    star: s,
    count: reviews.filter((r) => Number(r.rating) === s).length,
  }));
  const maxBucket = Math.max(1, ...dist.map((d) => d.count));

  return (
    <main className="min-h-[80vh] py-8 sm:py-12 px-4 sm:px-6" style={{ background: "#FFF7ED" }} data-testid="teacher-performance-page">
      <div className="max-w-4xl mx-auto">
        <Link
          to={`/teacher/${id}`}
          className="inline-flex items-center gap-1 text-sm font-bold hover:underline mb-5"
          style={{ color: "#13A699" }}
          data-testid="back-to-profile-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to profile
        </Link>

        {/* Teacher header */}
        <div className="bg-white rounded-3xl p-5 border border-black/5 flex items-center gap-4 shadow-sm">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={teacher.name}
              loading="lazy"
              decoding="async"
              className="h-16 w-16 rounded-full object-cover border border-black/10 select-none pointer-events-none"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          ) : (
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center font-display font-bold text-2xl"
              style={{ background: "#FFD708", color: "#13A699" }}
            >
              {(teacher.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: "#13A699" }}>
              {teacher.name}
            </h1>
            <p className="text-sm" style={{ color: "#13A69988" }}>Performance overview</p>
          </div>
        </div>

        {/* Aggregated rating — TOP of the page */}
        <div
          className="mt-5 rounded-3xl p-6 border shadow-sm"
          style={{ borderColor: "#13A69922", background: "linear-gradient(135deg, #FFF7ED, #FFD70833)" }}
          data-testid="perf-rating-card"
        >
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Star className="h-4 w-4" fill="#FFD708" stroke="#13A699" /> Aggregated Rating
          </h2>
          <div className="mt-3 flex items-end gap-3">
            <div className="flex items-baseline gap-1">
              <span className="font-display text-6xl font-bold" style={{ color: "#13A699" }}>
                {rating ? rating.toFixed(1) : "—"}
              </span>
              <span className="text-lg" style={{ color: "#13A69988" }}>/ 5</span>
            </div>
            <div className="text-sm" style={{ color: "#13A69988" }}>
              {ratingCount} review{ratingCount === 1 ? "" : "s"}
            </div>
          </div>

          {/* Star distribution */}
          {ratingCount > 0 && (
            <div className="mt-5 space-y-1.5" data-testid="rating-distribution">
              {dist.map((d) => (
                <div key={d.star} className="flex items-center gap-2 text-xs">
                  <span className="w-10 font-bold inline-flex items-center gap-0.5" style={{ color: "#13A699" }}>
                    {d.star} <Star className="h-3 w-3" fill="#FFD708" stroke="none" />
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-black/5 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(d.count / maxBucket) * 100}%`,
                        background: "#13A699",
                      }}
                    />
                  </div>
                  <span className="w-8 text-right" style={{ color: "#13A69988" }}>{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stats grid */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            icon={<BookOpen className="h-5 w-5" />}
            label="Courses Completed"
            value={coursesCompleted}
            testId="stat-courses"
          />
          <StatCard
            icon={<Sparkles className="h-5 w-5" />}
            label="Mentor Champ Badges"
            value={mentorChampCount}
            testId="stat-champ"
          />
          <StatCard
            icon={<Trophy className="h-5 w-5" />}
            label="Elite Status"
            value={eliteMentor ? "Yes" : "—"}
            testId="stat-elite"
          />
          <StatCard
            icon={<Award className="h-5 w-5" />}
            label="Conversion Points"
            value={points}
            testId="stat-points"
          />
        </div>

        {/* Badges showcase */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm" data-testid="badges-showcase">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Trophy className="h-4 w-4" /> Achievements
          </h2>
          {mentorChampCount === 0 && !eliteMentor && points === 0 ? (
            <p className="mt-3 text-sm" style={{ color: "#13A69988" }}>
              No achievements yet. Complete courses to earn Mentor Champ badges, get Elite Mentor at 5+ completions, and Conversion Points for converting demos into paid courses.
            </p>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {eliteMentor && (
                <span
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow"
                  style={{ background: "#13A699", color: "#FFD708" }}
                  data-testid="badge-elite-mentor"
                >
                  <Trophy className="h-4 w-4" /> Elite Mentor 🏆
                </span>
              )}
              {mentorChampCount > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow"
                  style={{ background: "#FFD708", color: "#13A699" }}
                  data-testid="badge-mentor-champ"
                >
                  <Sparkles className="h-4 w-4" /> Mentor Champ × {mentorChampCount}
                </span>
              )}
              {points > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold border-2"
                  style={{ borderColor: "#13A699", color: "#13A699", background: "#FFF7ED" }}
                  data-testid="badge-points"
                >
                  <Award className="h-4 w-4" /> {points} Conversion Points
                </span>
              )}
            </div>
          )}
          <div className="mt-5 grid sm:grid-cols-3 gap-3 text-xs" style={{ color: "#13A69988" }}>
            <div className="rounded-2xl border border-black/5 p-3">
              <div className="font-bold uppercase tracking-wider mb-1" style={{ color: "#13A699" }}>Mentor Champ</div>
              Awarded each time a course is completed.
            </div>
            <div className="rounded-2xl border border-black/5 p-3">
              <div className="font-bold uppercase tracking-wider mb-1" style={{ color: "#13A699" }}>Elite Mentor</div>
              Granted after &gt; 5 completed courses.
            </div>
            <div className="rounded-2xl border border-black/5 p-3">
              <div className="font-bold uppercase tracking-wider mb-1" style={{ color: "#13A699" }}>Conversion Points</div>
              +5 when a demo converts into a paid course (once per explorer).
            </div>
          </div>
        </div>

        {/* Reviews list */}
        <div className="mt-5 bg-white rounded-3xl p-6 border border-black/5 shadow-sm">
          <h2 className="font-display text-base font-bold uppercase tracking-widest inline-flex items-center gap-2" style={{ color: "#13A699" }}>
            <Star className="h-4 w-4" fill="#FFD708" stroke="#13A699" /> Reviews
          </h2>
          {reviews.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: "#13A69988" }}>No reviews yet.</p>
          ) : (
            <ul className="mt-3 space-y-3" data-testid="perf-reviews-list">
              {reviews.map((r, idx) => (
                <li key={idx} className="rounded-xl border border-black/5 p-3" data-testid={`perf-review-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Star className="h-4 w-4" fill="#FFD708" stroke="#13A699" />
                    <span className="font-bold text-sm" style={{ color: "#13A699" }}>{r.rating}/5</span>
                    <span className="text-xs" style={{ color: "#13A69988" }}>by {r.parent_name}</span>
                    {r.kind === "course" && r.course_title && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: "#FFD708", color: "#13A699" }}>
                        Course: {r.course_title}
                      </span>
                    )}
                  </div>
                  {r.review && <p className="text-sm mt-1" style={{ color: "#13A699" }}>{r.review}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-black/5 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2" style={{ color: "#13A699" }}>
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
      <div className="mt-1 font-display text-2xl font-bold" style={{ color: "#13A699" }}>
        {value}
      </div>
    </div>
  );
}
